import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import RequireAuth from "./components/RequireAuth";
import RequireRole from "./components/RequireRole";
import AppShell from "./components/AppShell";

import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import UserRegistration from "./pages/UserRegistration";
import SelectCompany from "./pages/SelectCompany";

import Admin from "./pages/Admin";
import BillingPage from "./pages/BillingPage";

import Home from "./pages/Home";
import MyFleet from "./pages/MyFleet";
import AddVessel from "./pages/AddVessel";
import Companies from "./pages/Companies";
import AddCompany from "./pages/AddCompany";
import EditCompany from "./pages/EditCompany";
import EditVessel from "./pages/EditVessel";
import Templates from "./pages/Templates";
import TemplateBuilder from "./pages/TemplateBuilder";
import TemplateRules from "./pages/TemplateRules";
import TemplateEditPage from "./pages/TemplateEditPage";
import Inspections from "./pages/Inspections";
import InspectionDetail from "./pages/InspectionDetail";
import UserProfile from "./pages/UserProfile";
import ChangeCredentials from "./pages/ChangeCredentials";
import QuestionsPanel from "./components/india/QuestionsPanel";
import EvidenceUploadPanel from "./components/india/EvidenceUploadPanel";
import CloseOutOpenPointsPanel from "./components/india/CloseOutOpenPointsPanel";
import InspectionSetupPage from "./pages/india/InspectionSetupPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import PlansPage from "./pages/PlansPage";

const ANY_MEMBER = ["any"];

function RoleBasedRedirect() {
  const { user, activeCompany } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!activeCompany) return <Navigate to="/login" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<UserRegistration />} />
          <Route path="/select-company" element={<SelectCompany />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/plans" element={<PlansPage />} />

          {/* Protected app shell */}
          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            {/* Dashboard — was "/" */}
            <Route path="/dashboard" element={<Home />} />

            {/* Vessels */}
            <Route path="/vessels" element={
              <RequireRole allowed={ANY_MEMBER}><MyFleet /></RequireRole>
            } />
            <Route path="/vessels/add" element={
              <RequireRole allowed={ANY_MEMBER}><AddVessel /></RequireRole>
            } />
            <Route path="/vessels/:id/edit" element={
              <RequireRole allowed={ANY_MEMBER}><EditVessel /></RequireRole>
            } />

            {/* Companies */}
            <Route path="/companies" element={
              <RequireRole allowed={ANY_MEMBER}><Companies /></RequireRole>
            } />
            <Route path="/add-company" element={
              <RequireRole allowed={ANY_MEMBER}><AddCompany /></RequireRole>
            } />
            <Route path="/companies/:id/edit" element={
              <RequireRole allowed={ANY_MEMBER}><EditCompany /></RequireRole>
            } />

            {/* Templates */}
            <Route path="/templates" element={
              <RequireRole allowed={ANY_MEMBER}><Templates /></RequireRole>
            } />
            <Route path="/templates/new" element={
              <RequireRole allowed={ANY_MEMBER}><TemplateBuilder /></RequireRole>
            } />
            <Route path="/templates/:templateId/rules" element={
              <RequireRole allowed={ANY_MEMBER}><TemplateRules /></RequireRole>
            } />
            <Route path="/templates/:templateId/edit" element={
              <RequireRole allowed={ANY_MEMBER}><TemplateEditPage /></RequireRole>
            } />

            {/* Inspections */}
            <Route path="/inspections" element={
              <RequireRole allowed={ANY_MEMBER}><Inspections /></RequireRole>
            } />
            <Route path="/inspections/:inspectionId/setup" element={
              <RequireRole allowed={ANY_MEMBER}><InspectionSetupPage /></RequireRole>
            } />
            <Route path="/inspections/:inspectionId/*" element={
              <RequireRole allowed={ANY_MEMBER}><InspectionDetail /></RequireRole>
            }>
              <Route index element={<Navigate to="questions" replace />} />
              <Route path="questions" element={<QuestionsPanel />} />
              <Route path="evidence" element={<EvidenceUploadPanel />} />
              <Route path="closeout" element={<CloseOutOpenPointsPanel />} />
            </Route>

            {/* Profile */}
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/profile/security" element={<ChangeCredentials />} />

            {/* Billing */}
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/billing/success" element={<BillingPage />} />
          </Route>

          <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
          
          {/* Fallback */}
          <Route path="*" element={<RoleBasedRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}